























































































































































































































































































































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import quotationServices from '@/services/quotation-services';
import notificationSound from '@/utility/notification-sound';
import { eventBus } from '@/main';
import { APP_CONFIG } from '@/config/app-config';

interface StaffInfo {
    id?: number;
    role: string;
    role_name: string;
    username: string;
    full_name: string;
    avatar?: string;
}

interface PendingAttachment {
    id: string;
    file: File;
    name: string;
    size: number;
    type: string;
    status: 'uploading' | 'success' | 'error';
    url?: string;
    path?: string;
}

@Component
export default class CustomerChat extends Vue {
    @Prop({ required: true })
    private quotationId!: number;

    @Prop({ default: () => ({}) })
    private quotation!: any;

    private comments: any[] = [];
    private commentText: string = '';
    private isSending: boolean = false;
    private isRefreshing: boolean = false;
    private isLoadingInitial: boolean = true;
    private timer: any = null;

    // Threading
    private replyTarget: any = null;

    // Attachments
    private pendingAttachments: PendingAttachment[] = [];

    // Mention
    private mentionDropdownActive: boolean = false;
    private mentionFilterText: string = '';
    private mentionCursorPosition: number = 0;

    // Lightbox
    private lightboxUrl: string | null = null;

    // Track known comment IDs for sound and popup notifications
    private knownCommentIds: { [key: number]: boolean } = {};
    private isFirstLoadDone: boolean = false;
    private hasNotificationPermission: boolean = false;

    private get topLevelComments(): any[] {
        if (!this.comments || !Array.isArray(this.comments)) return [];
        return this.comments.filter((c: any) => !c.parent);
    }

    private repliesFor(parentId: number): any[] {
        if (!this.comments || !Array.isArray(this.comments) || !parentId) return [];
        return this.comments.filter((c: any) => c.parent === parentId);
    }

    private get assignedStaffList(): StaffInfo[] {
        if (this.quotation && this.quotation.assigned_staff && Array.isArray(this.quotation.assigned_staff)) {
            return this.quotation.assigned_staff;
        }
        return [];
    }

    private get filteredMentions(): StaffInfo[] {
        const staff = this.assignedStaffList;
        if (!this.mentionFilterText) return staff;
        const q = this.mentionFilterText.toLowerCase();
        return staff.filter((s: StaffInfo) => {
            const u = (s.username || '').toLowerCase();
            const f = (s.full_name || '').toLowerCase();
            return u.indexOf(q) !== -1 || f.indexOf(q) !== -1;
        });
    }

    private get hasReadyAttachments(): boolean {
        return this.pendingAttachments.some((a: PendingAttachment) => a.status === 'success');
    }

    private async created() {
        this.checkNotificationPermission();
        await this.loadComments(false);
        this.isLoadingInitial = false;

        // Bắt đầu polling nhận tin mới mỗi 8 giây
        this.timer = setInterval(() => {
            this.loadComments(true);
        }, 8000);

        window.addEventListener('keydown', this.handleGlobalKeydown);
    }

    private destroyed() {
        if (this.timer) {
            clearInterval(this.timer);
        }
        window.removeEventListener('keydown', this.handleGlobalKeydown);
    }

    private checkNotificationPermission() {
        this.hasNotificationPermission = notificationSound.hasBrowserPermission;
    }

    private async requestNotification() {
        notificationSound.initAudio();
        const res = await notificationSound.requestBrowserPermission();
        this.hasNotificationPermission = (res === 'granted');
        if (this.hasNotificationPermission) {
            notificationSound.playChime();
            notificationSound.showBrowserNotification('Thông báo UNS', {
                body: 'Bạn đã kích hoạt thành công chuông và thông báo trình duyệt!',
            });
        }
    }

    private handleGlobalKeydown(e: KeyboardEvent) {
        if (e.key === 'Escape' && this.lightboxUrl) {
            this.closeLightbox();
        }
    }

    private getAvatarUrl(comment: any): string {
        if (!comment) return '';

        // 1. Kiểm tra trực tiếp trên dữ liệu comment
        if (comment.user_avatar && typeof comment.user_avatar === 'string' && comment.user_avatar.trim()) {
            return comment.user_avatar.trim();
        }
        if (comment.avatar && typeof comment.avatar === 'string' && comment.avatar.trim()) {
            return comment.avatar.trim();
        }
        if (comment.extra && comment.extra.user_avatar && typeof comment.extra.user_avatar === 'string' && comment.extra.user_avatar.trim()) {
            return comment.extra.user_avatar.trim();
        }
        if (comment.extra && comment.extra.avatar && typeof comment.extra.avatar === 'string' && comment.extra.avatar.trim()) {
            return comment.extra.avatar.trim();
        }

        // 2. Nếu là bình luận từ nhân viên, đối chiếu với danh sách nhân viên phụ trách báo giá
        if (!comment.is_from_customer) {
            const username = (comment.username || '').toLowerCase().trim();
            const userDisplay = (comment.user_display || '').toLowerCase().trim();

            const staffList = this.assignedStaffList || [];
            const matchedStaff = staffList.find((s: StaffInfo) => {
                const sUser = (s.username || '').toLowerCase().trim();
                const sName = (s.full_name || '').toLowerCase().trim();
                return (username && (sUser === username || sName === username)) ||
                       (userDisplay && (sUser === userDisplay || sName === userDisplay));
            });

            if (matchedStaff && matchedStaff.avatar && typeof matchedStaff.avatar === 'string' && matchedStaff.avatar.trim()) {
                return matchedStaff.avatar.trim();
            }
        }

        return '';
    }

    private onAvatarError(event: Event) {
        const target = event.target as HTMLElement;
        if (target) {
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
                const icon = parent.querySelector('i.fallback-icon');
                if (icon) {
                    (icon as HTMLElement).style.display = 'inline-block';
                }
            }
        }
    }

    private async loadComments(isSilent: boolean = false) {
        if (!isSilent) this.isRefreshing = true;
        try {
            const res: any = await quotationServices.getComments(this.quotationId);
            if (res && res.data && res.data.status === 200) {
                const freshComments: any[] = res.data.data || [];
                const oldLen = this.comments.length;

                if (this.isFirstLoadDone && isSilent) {
                    // Kiểm tra xem có comment mới từ nhân viên không
                    const newStaffComments = freshComments.filter((c: any) => {
                        return !this.knownCommentIds[c.id] && !c.is_from_customer;
                    });

                    if (newStaffComments.length > 0) {
                        // Kích hoạt chuông âm thanh
                        notificationSound.playChime();

                        // Hiển thị Browser Web Notification nếu được cấp quyền
                        const latest = newStaffComments[newStaffComments.length - 1];
                        const title = '[UNS] ' + (latest.user_display || 'Nhân viên phụ trách') + ' đã gửi phản hồi';
                        const body = latest.content || 'Đã gửi tệp đính kèm trong chi tiết báo giá.';
                        notificationSound.showBrowserNotification(title, { body: body });

                        // Đồng bộ cập nhật quả chuông thông báo & toast header
                        eventBus.$emit(APP_CONFIG.eventBusAction.reloadNotification);
                    }
                }

                // Cập nhật danh sách comment IDs đã biết
                for (let i = 0; i < freshComments.length; i++) {
                    const c = freshComments[i];
                    if (c && c.id) {
                        this.knownCommentIds[c.id] = true;
                    }
                }

                this.comments = freshComments;
                this.isFirstLoadDone = true;

                if (this.comments.length > oldLen) {
                    this.scrollToBottom();
                }
            }
        } catch (e) {
            // silent catch
        } finally {
            if (!isSilent) this.isRefreshing = false;
        }
    }

    // ─── Threading Methods ──────────────────────────────────────────────────
    private setReplyTarget(comment: any) {
        this.replyTarget = comment;
        this.$nextTick(() => {
            const input = this.$refs.commentInput as HTMLTextAreaElement;
            if (input) {
                input.focus();
            }
        });
    }

    private cancelReply() {
        this.replyTarget = null;
    }

    private getSnippet(content: string): string {
        if (!content) return 'tệp đính kèm';
        return content.length > 50 ? content.substring(0, 50) + '...' : content;
    }

    // ─── Send Comment ───────────────────────────────────────────────────────
    private onKeydown(e: KeyboardEvent) {
        if (e.keyCode === 13 && !e.shiftKey) {
            e.preventDefault();
            this.sendComment();
        }
    }

    private async sendComment() {
        const text = this.commentText.trim();
        const readyAtts = this.pendingAttachments
            .filter((a: PendingAttachment) => a.status === 'success')
            .map((a: PendingAttachment) => ({
                name: a.name,
                url: a.url,
                size: a.size,
                type: a.type,
                path: a.path,
            }));

        if (!text && readyAtts.length === 0) return;
        if (this.isSending) return;

        // Nếu có tệp đang upload dở, chờ hoặc nhắc người dùng
        const uploadingCount = this.pendingAttachments.filter((a: PendingAttachment) => a.status === 'uploading').length;
        if (uploadingCount > 0) {
            alert('Vui lòng đợi tệp tải lên hoàn tất trước khi gửi.');
            return;
        }

        this.isSending = true;
        try {
            const parentId = this.replyTarget ? this.replyTarget.id : undefined;
            const extra = readyAtts.length > 0 ? { attachments: readyAtts } : undefined;

            const res: any = await quotationServices.addComment(this.quotationId, text, parentId, extra);
            if (res && res.data && res.data.status === 200) {
                this.commentText = '';
                this.replyTarget = null;
                this.pendingAttachments = [];
                this.mentionDropdownActive = false;

                await this.loadComments(false);
                this.scrollToBottom();
            } else {
                const reason = (res && res.data && res.data.data) ? res.data.data : 'Lỗi gửi bình luận';
                alert(typeof reason === 'string' ? reason : 'Lỗi gửi bình luận');
            }
        } catch (e) {
            const err = e as any;
            const errMsg = (err && err.response && err.response.data && err.response.data.data) ? err.response.data.data : 'Lỗi gửi bình luận';
            alert(errMsg);
        } finally {
            this.isSending = false;
        }
    }

    private scrollToBottom() {
        this.$nextTick(() => {
            const container = this.$refs.messageContainer as HTMLElement;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    }

    // ─── Clipboard Paste & Attachments ──────────────────────────────────────
    private handlePaste(event: ClipboardEvent) {
        const clipboard = event.clipboardData;
        if (!clipboard || !clipboard.items) return;

        const items = clipboard.items;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf('image') !== -1) {
                const file = item.getAsFile();
                if (file) {
                    event.preventDefault();
                    this.uploadPendingFile(file, 'pasted_image_' + Date.now() + '.png');
                }
            }
        }
    }

    private triggerFileInput() {
        const input = this.$refs.fileInput as HTMLInputElement;
        if (input) {
            input.click();
        }
    }

    private handleFileChange(event: Event) {
        const target = event.target as HTMLInputElement;
        const files = target.files;
        if (!files || files.length === 0) return;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploadPendingFile(file, file.name);
        }

        target.value = ''; // Reset input
    }

    private async uploadPendingFile(file: File, fallbackName: string) {
        const attId = Math.random().toString(36).substring(2, 9);
        const pendingItem: PendingAttachment = {
            id: attId,
            file: file,
            name: file.name || fallbackName,
            size: file.size,
            type: file.type || 'application/octet-stream',
            status: 'uploading',
        };
        this.pendingAttachments.push(pendingItem);

        try {
            const res: any = await quotationServices.uploadImage(file);
            const found = this.pendingAttachments.find((a: PendingAttachment) => a.id === attId);
            if (found) {
                if (res && res.data && res.data.status === 200 && res.data.data) {
                    found.status = 'success';
                    found.url = res.data.data.url;
                    found.path = res.data.data.path;
                    if (res.data.data.name) found.name = res.data.data.name;
                    if (res.data.data.size) found.size = res.data.data.size;
                    if (res.data.data.type) found.type = res.data.data.type;
                } else {
                    found.status = 'error';
                }
            }
        } catch (e) {
            const found = this.pendingAttachments.find((a: PendingAttachment) => a.id === attId);
            if (found) {
                found.status = 'error';
            }
        }
    }

    private removePendingAttachment(id: string) {
        this.pendingAttachments = this.pendingAttachments.filter((a: PendingAttachment) => a.id !== id);
    }

    // ─── Mention Logic ──────────────────────────────────────────────────────
    private handleCommentInput(e: Event) {
        const textarea = e.target as HTMLTextAreaElement;
        if (!textarea) return;

        const val = textarea.value;
        const cursor = textarea.selectionStart;

        const textBeforeCursor = val.substring(0, cursor);
        const words = textBeforeCursor.split(/\s/);
        const lastWord = words[words.length - 1];

        if (lastWord && lastWord.charAt(0) === '@') {
            this.mentionDropdownActive = true;
            this.mentionFilterText = lastWord.substring(1);
            this.mentionCursorPosition = cursor;
        } else {
            this.mentionDropdownActive = false;
        }
    }

    private insertMention(staff: StaffInfo) {
        const textarea = this.$refs.commentInput as HTMLTextAreaElement;
        if (!textarea) return;

        const val = this.commentText;
        const cursor = this.mentionCursorPosition;

        const textBeforeCursor = val.substring(0, cursor);
        const words = textBeforeCursor.split(/\s/);
        const lastWord = words[words.length - 1];

        const startReplaceIdx = cursor - lastWord.length;
        const mentionText = '@' + staff.username + ' ';

        this.commentText = val.substring(0, startReplaceIdx) + mentionText + val.substring(cursor);
        this.mentionDropdownActive = false;

        this.$nextTick(() => {
            textarea.focus();
            const newCursorPos = startReplaceIdx + mentionText.length;
            textarea.selectionStart = newCursorPos;
            textarea.selectionEnd = newCursorPos;
        });
    }

    private insertStaffMention(staff: StaffInfo) {
        const mentionTag = '@' + staff.username + ' ';
        this.commentText = (this.commentText ? this.commentText + ' ' : '') + mentionTag;
        this.$nextTick(() => {
            const textarea = this.$refs.commentInput as HTMLTextAreaElement;
            if (textarea) {
                textarea.focus();
                textarea.selectionStart = textarea.selectionEnd = this.commentText.length;
            }
        });
    }

    private getStaffRoleShort(role: string): string {
        if (role === 'sale') return 'Sale';
        if (role === 'sale_admin') return 'SA';
        if (role === 'xnk') return 'XNK';
        return 'NV';
    }

    private getStaffChipClass(role: string): string {
        if (role === 'sale') return 'chip-sale';
        if (role === 'sale_admin') return 'chip-sa';
        if (role === 'xnk') return 'chip-xnk';
        return 'chip-default';
    }

    private formatContentWithMentions(content: string): string {
        if (!content) return '';
        // Escape HTML to prevent XSS
        const escaped = content
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        // Highlight @username
        return escaped.replace(/@([a-zA-Z0-9_]+)/g, '<span class="mention-highlight">@$1</span>');
    }

    // ─── Helpers: File, Attachments & Lightbox ───────────────────────────────
    private hasAttachments(comment: any): boolean {
        return !!(comment && comment.extra && comment.extra.attachments && comment.extra.attachments.length > 0);
    }

    private isImageFile(type: string, name: string): boolean {
        if (type && type.indexOf('image/') === 0) return true;
        if (!name) return false;
        const ext = name.split('.').pop() || '';
        return /^(jpg|jpeg|png|gif|webp|svg)$/i.test(ext.toLowerCase());
    }

    private getImages(comment: any): any[] {
        if (!this.hasAttachments(comment)) return [];
        return comment.extra.attachments.filter((a: any) => this.isImageFile(a.type, a.name));
    }

    private getDocuments(comment: any): any[] {
        if (!this.hasAttachments(comment)) return [];
        return comment.extra.attachments.filter((a: any) => !this.isImageFile(a.type, a.name));
    }

    private getDocumentIcon(name: string, type: string): string {
        const ext = (name ? name.split('.').pop() : '') || '';
        const lower = ext.toLowerCase();
        if (lower === 'pdf') return 'fa-file-pdf-o text-danger';
        if (lower === 'doc' || lower === 'docx') return 'fa-file-word-o text-primary';
        if (lower === 'xls' || lower === 'xlsx') return 'fa-file-excel-o text-success';
        if (lower === 'zip' || lower === 'rar') return 'fa-file-archive-o text-warning';
        return 'fa-file-text-o text-muted';
    }

    private formatFileSize(bytes: number): string {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    private formatDateTime(dt: string): string {
        if (!dt) return '';
        try {
            const d = new Date(dt);
            if (isNaN(d.getTime())) return dt;
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes} ${day}/${month}/${year}`;
        } catch (e) {
            return dt;
        }
    }

    private openLightbox(url: string) {
        this.lightboxUrl = url;
    }

    private closeLightbox() {
        this.lightboxUrl = null;
    }
}
